(() => {
  if (customElements.get('beststar-purchase-bar')) return;

  class BestStarPurchaseBar extends HTMLElement {
    static activeOperation = null;

    static couponEligible(config, now = Date.now()) {
      const coupon = config.coupon;
      const starts = Date.parse(coupon?.startsAt || '');
      const ends = coupon?.endsAt ? Date.parse(coupon.endsAt) : Infinity;
      return config.currency === 'KRW' && coupon?.status === 'ACTIVE' && coupon.scopeMatches === true &&
        typeof coupon.code === 'string' && coupon.code.length > 0 && Number.isFinite(starts) &&
        starts <= now && ends > now;
    }

    static verifiedRoute(routes, variant, productId, origin) {
      if (!Array.isArray(routes) || !/^\d+$/.test(String(productId))) return null;
      const matches = routes.filter((item) => item && typeof item === 'object' &&
        item.shopifyVariantId === 'gid://shopify/ProductVariant/' + variant?.id &&
        typeof item.vendorItemId === 'string' && item.vendorItemId === variant?.sku);
      if (matches.length !== 1) return null;
      const row = matches[0];
      try {
        const url = new URL(row.coupangUrl, origin);
        if (url.protocol !== 'https:' || url.username || url.password || url.port ||
          !['www.coupang.com', 'coupang.com', 'link.coupang.com'].includes(url.hostname)) return null;
        if (!/^\/vp\/products\/\d+$/.test(url.pathname) ||
          url.searchParams.get('vendorItemId') !== row.vendorItemId) return null;
        return url.href;
      } catch {
        return null;
      }
    }

    static validCart(cart) {
      return cart && typeof cart.currency === 'string' && /^[A-Z]{3}$/.test(cart.currency) &&
        Number.isSafeInteger(cart.item_count) && cart.item_count >= 0 &&
        Array.isArray(cart.items) && cart.items.every((item) =>
          /^\d+$/.test(String(item.variant_id || item.id)) && Number.isSafeInteger(item.quantity) && item.quantity > 0) &&
        cart.items.reduce((count, item) => count + item.quantity, 0) === cart.item_count;
    }

    static lineIdentity(item) {
      const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
      return JSON.stringify([String(item.variant_id || item.id), properties,
        String(item.selling_plan_allocation?.selling_plan?.id || '')]);
    }

    static confirmsAddition(before, after, variantId, quantity) {
      if (!this.validCart(before) || !this.validCart(after) || after.item_count !== before.item_count + quantity) return false;
      const variantQuantity = (cart) => cart.items.reduce((sum, item) =>
        sum + (String(item.variant_id || item.id) === String(variantId) ? item.quantity : 0), 0);
      if (variantQuantity(after) !== variantQuantity(before) + quantity) return false;
      const totals = (cart) => {
        const map = new Map();
        for (const item of cart.items) {
          const key = this.lineIdentity(item);
          map.set(key, (map.get(key) || 0) + item.quantity);
        }
        return map;
      };
      const oldLines = totals(before);
      const newLines = totals(after);
      return [...oldLines].every(([key, count]) => (newLines.get(key) || 0) >= count);
    }

    static checkoutDestination(config, origin, now = Date.now()) {
      const root = new URL(config.rootUrl || '/', origin);
      if (root.origin !== origin) throw new Error('Invalid store route');
      const checkout = root.pathname.replace(/\/$/, '') + '/checkout';
      if (!this.couponEligible(config, now)) return new URL(checkout, origin).href;
      const url = new URL('/discount/' + encodeURIComponent(config.coupon.code), origin);
      url.searchParams.set('redirect', checkout);
      return url.href;
    }

    connectedCallback() {
      try {
        this.config = JSON.parse(this.querySelector('[data-bs-purchase-config]').textContent);
      } catch {
        return;
      }
      this.form = document.getElementById(this.dataset.formId);
      this.owner = this.form?.closest('product-form');
      this.info = this.closest('product-info');
      this.button = this.querySelector('[data-bs-buy-now]');
      this.coupang = this.querySelector('[data-bs-coupang-route]');
      this.bar = this.querySelector('.bs-purchase-bar');
      this.routes = [];
      this.onBuy = () => this.purchase();
      this.onChange = () => this.sync();
      this.onFocus = () => requestAnimationFrame(() => this.updateViewport());
      this.onPageShow = (event) => {
        // A checkout/cart round trip must not restore an old in-flight UI from bfcache.
        if (event.persisted && (this.busy || this.reviewRequired || this.owner?.beststarCartNeedsRefresh)) location.reload();
      };
      this.button.addEventListener('click', this.onBuy);
      this.info?.addEventListener('change', this.onChange);
      this.info?.addEventListener('input', this.onChange);
      document.addEventListener('focusin', this.onFocus);
      document.addEventListener('focusout', this.onFocus);
      window.addEventListener('focus', this.onFocus);
      window.addEventListener('blur', this.onFocus);
      window.visualViewport?.addEventListener('resize', this.onFocus);
      window.visualViewport?.addEventListener('scroll', this.onFocus);
      window.addEventListener('pageshow', this.onPageShow);
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId === this.dataset.sectionId) this.sync();
        });
      }
      this.observer = new MutationObserver(() => this.sync());
      if (this.owner) this.observer.observe(this.owner, {
        subtree: true, attributes: true, attributeFilter: ['value', 'disabled', 'aria-disabled'], childList: true,
      });
      this.resizeObserver = new ResizeObserver(() => this.updateViewport());
      this.resizeObserver.observe(this.bar);
      this.couponTimer = setInterval(() => this.sync(), 60000);
      this.setAttribute('data-enhanced', '');
      this.sync();
      this.loadRoutes();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onBuy);
      this.info?.removeEventListener('change', this.onChange);
      this.info?.removeEventListener('input', this.onChange);
      document.removeEventListener('focusin', this.onFocus);
      document.removeEventListener('focusout', this.onFocus);
      window.removeEventListener('focus', this.onFocus);
      window.removeEventListener('blur', this.onFocus);
      window.visualViewport?.removeEventListener('resize', this.onFocus);
      window.visualViewport?.removeEventListener('scroll', this.onFocus);
      window.removeEventListener('pageshow', this.onPageShow);
      this.unsubscribe?.();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      clearInterval(this.couponTimer);
      if (!document.querySelector('beststar-purchase-bar[data-enhanced]')) {
        document.documentElement.style.removeProperty('--bs-purchase-bar-height');
      }
    }

    text(key) { return this.config.text[key]; }

    setText(element, text) {
      if (element && element.textContent !== text) element.textContent = text;
    }

    selectedVariant() {
      const id = String(this.form?.elements.namedItem('id')?.value || '');
      return this.config.variants.find((variant) => String(variant.id) === id);
    }

    coreCompatible() {
      return this.form && this.owner && this.info &&
        typeof this.owner.refreshBeststarCartAfterAddError === 'function' &&
        typeof this.owner.blockBeststarAddUntilRefresh === 'function';
    }

    sync() {
      const variant = this.selectedVariant();
      const add = this.owner?.submitButton;
      const unavailable = !this.coreCompatible() || this.dataset.purchaseBlocked === 'true' ||
        !variant?.available || !add || add.disabled || add.getAttribute('aria-disabled') === 'true' ||
        this.form?.elements.namedItem('id')?.disabled || this.info?.pendingRequestUrl ||
        this.owner?.beststarCartNeedsRefresh || this.reviewRequired;
      const disabled = Boolean(this.busy || unavailable);
      if (this.button.disabled !== disabled) this.button.disabled = disabled;
      this.button.setAttribute('aria-busy', String(Boolean(this.busy)));
      this.toggleAttribute('data-busy', Boolean(this.busy));
      let label = this.text('buy_now');
      if (this.busy) label = this.text('working');
      else if (this.reviewRequired || this.owner?.beststarCartNeedsRefresh) label = this.text('review_first');
      else if (this.info?.pendingRequestUrl) label = this.text('pending_short');
      else if (!variant?.available || add?.disabled) label = this.text('unavailable');
      this.setText(this.querySelector('[data-bs-buy-label]'), label);
      const coupon = BestStarPurchaseBar.couponEligible(this.config);
      const couponLine = this.querySelector('[data-bs-coupon-line]');
      if (couponLine.hidden === coupon) couponLine.hidden = !coupon;
      this.setText(this.querySelector('[data-bs-purchase-terms]'), coupon ? this.text('coupon_terms') : this.text('normal_terms'));
      const route = !this.busy && variant && BestStarPurchaseBar.verifiedRoute(this.routes, variant, this.config.productId, location.origin);
      if (route) {
        if (this.coupang.getAttribute('href') !== route) this.coupang.setAttribute('href', route);
        this.coupang.removeAttribute('aria-disabled');
        this.coupang.removeAttribute('tabindex');
        this.coupang.title = '';
      } else {
        this.coupang.removeAttribute('href');
        if (this.coupang.getAttribute('aria-disabled') !== 'true') this.coupang.setAttribute('aria-disabled', 'true');
        this.coupang.setAttribute('tabindex', '-1');
        this.coupang.title = this.text('coupang_unavailable');
      }
      this.updateViewport();
    }

    loadRoutes() {
      const map = this.config.coupangRoutes;
      this.routes = map?.version === 1 && map.source === 'Coupang' && Array.isArray(map.options) ? map.options : [];
      this.sync();
    }

    updateViewport() {
      if (!this.bar) return;
      const height = Math.ceil(this.bar.getBoundingClientRect().height);
      const value = height + 'px';
      if (document.documentElement.style.getPropertyValue('--bs-purchase-bar-height') !== value) {
        document.documentElement.style.setProperty('--bs-purchase-bar-height', value);
      }
      const active = document.activeElement;
      const editing = active && !this.contains(active) &&
        (active.matches('input, textarea, select, iframe') || active.isContentEditable);
      const viewport = window.visualViewport;
      const keyboard = viewport && viewport.height < document.documentElement.clientHeight * .72;
      this.toggleAttribute('data-suspended', Boolean(editing || keyboard));
      if (!editing && !keyboard && active && active !== document.body && !this.contains(active)) {
        const rect = active.getBoundingClientRect();
        if (rect.bottom > this.bar.getBoundingClientRect().top && rect.top < innerHeight) {
          active.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      }
    }

    showError(message, review) {
      const wrapper = this.querySelector('[data-bs-purchase-feedback]');
      const error = this.querySelector('[data-bs-purchase-error]');
      wrapper.hidden = false;
      this.setText(error, message || this.text('error'));
      this.querySelector('[data-bs-purchase-review]').hidden = !review;
      error.focus({ preventScroll: true });
      error.scrollIntoView({ block: 'center', behavior: 'auto' });
    }

    cartOperationPending() {
      return [...document.querySelectorAll('cart-items, cart-drawer-items')].some((cart) =>
        cart.beststarCartConfirmed === false || cart.beststarUpdatePending) ||
        [...document.querySelectorAll('product-form')].some((form) =>
          form.submitButton?.classList.contains('loading') || form.submitButton?.getAttribute('aria-disabled') === 'true');
    }

    lockControls() {
      const controls = [...document.querySelectorAll('product-form button[type="submit"], product-info variant-selects input, product-info variant-selects select, product-info quantity-input input, product-info quantity-input button, cart-items input, cart-items button, cart-drawer-items input, cart-drawer-items button')];
      const saved = controls.map((element) => ({ element, disabled: element.disabled, aria: element.getAttribute('aria-disabled') }));
      for (const { element } of saved) {
        element.disabled = true;
        element.setAttribute('aria-disabled', 'true');
      }
      this.guardSubmit = (event) => {
        if (event.target.matches('form[data-type="add-to-cart-form"], form[action*="/cart"]')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      this.guardRemove = (event) => {
        if (event.target.closest('cart-remove-button')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      document.addEventListener('submit', this.guardSubmit, true);
      document.addEventListener('click', this.guardRemove, true);
      return () => {
        document.removeEventListener('submit', this.guardSubmit, true);
        document.removeEventListener('click', this.guardRemove, true);
        for (const { element, disabled, aria } of saved) {
          if (!element.isConnected) continue;
          if (element === this.owner.submitButton && this.owner.beststarCartNeedsRefresh) continue;
          element.disabled = disabled;
          if (aria === null) element.removeAttribute('aria-disabled');
          else element.setAttribute('aria-disabled', aria);
        }
      };
    }

    async readCart() {
      const url = new URL(this.config.cartUrl + '.js', location.origin);
      if (url.origin !== location.origin) throw new Error(this.text('error'));
      const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(this.text('error'));
      const cart = await response.json();
      if (!BestStarPurchaseBar.validCart(cart)) throw new Error(this.text('error'));
      return cart;
    }

    async purchase() {
      if (this.busy || BestStarPurchaseBar.activeOperation) return;
      this.sync();
      if (this.button.disabled) return;
      if (this.cartOperationPending()) return this.showError(this.text('pending'), false);
      const variant = this.selectedVariant();
      const quantityInput = this.form.elements.namedItem('quantity');
      if (quantityInput && !quantityInput.checkValidity()) {
        quantityInput.reportValidity();
        return;
      }
      const formData = new FormData(this.form);
      const rawQuantity = formData.get('quantity');
      const quantity = rawQuantity === null ? 1 : Number(rawQuantity);
      if (!variant || String(formData.get('id')) !== String(variant.id) || !Number.isSafeInteger(quantity) || quantity < 1) {
        return this.showError(this.text('quantity_invalid'), false);
      }
      formData.set('quantity', String(quantity));
      const addUrl = new URL(window.routes?.cart_add_url || this.config.cartAddUrl, location.origin);
      if (addUrl.origin !== location.origin || !/\/cart\/add(?:\.js)?$/.test(addUrl.pathname)) {
        return this.showError(this.text('error'), false);
      }
      this.busy = true;
      BestStarPurchaseBar.activeOperation = this;
      const unlock = this.lockControls();
      this.querySelector('[data-bs-purchase-feedback]').hidden = true;
      this.setText(this.querySelector('[data-bs-purchase-status]'), this.text('working'));
      this.sync();
      let submitted = false;
      let navigating = false;
      let linesUpdate;
      try {
        const before = await this.readCart();
        linesUpdate = this.owner.createCartLinesUpdateEvent?.(variant.id, quantity);
        // One mutation only. Never clear, replace, update quantities, or retry an add.
        submitted = true;
        const response = await fetch(addUrl.href, {
          method: 'POST', credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData,
        });
        const added = await response.json();
        if (!response.ok || added.status) throw new Error(added.description || added.message || this.text('error'));
        if (String(added.variant_id || added.id) !== String(variant.id)) throw new Error(this.text('review_required'));
        const after = await this.readCart();
        if (!BestStarPurchaseBar.confirmsAddition(before, after, variant.id, quantity)) throw new Error(this.text('review_required'));
        if (typeof publish === 'function') {
          await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: String(variant.id), cartData: after });
        }
        const eventType = window.StandardEvents?.CartLinesUpdateEvent;
        if (linesUpdate && eventType) linesUpdate.resolve({ cart: eventType.createCartFromAjaxResponse(after) });
        // Native Shopify discount share route preserves this cart and opens checkout.
        const destination = BestStarPurchaseBar.checkoutDestination({ ...this.config, currency: after.currency }, location.origin);
        location.assign(destination);
        navigating = true;
      } catch (error) {
        linesUpdate?.reject(error);
        if (submitted) {
          this.reviewRequired = true;
          // Reuse v3 reconciliation. Ambiguous/partial adds require cart review, never a second POST.
          try { await this.owner.refreshBeststarCartAfterAddError(variant.id, error.message, true); } catch { /* keep blocked */ }
          this.owner.blockBeststarAddUntilRefresh(error.message, true);
          this.showError(this.text('review_required'), true);
        } else {
          this.showError(error.message || this.text('error'), false);
        }
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartError, {
          source: 'beststar-buy-now', productVariantId: String(variant.id), errors: error.message, message: error.message,
        });
      } finally {
        if (!navigating) unlock();
        this.busy = navigating;
        if (!navigating) BestStarPurchaseBar.activeOperation = null;
        this.setText(this.querySelector('[data-bs-purchase-status]'), navigating ? this.text('working') : '');
        this.sync();
      }
    }
  }

  customElements.define('beststar-purchase-bar', BestStarPurchaseBar);
})();
